import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik } from 'formik';
import {
  AtsSettingsDocument,
  useSetAtsStageRejectionReasonsMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  Button,
  EditFormWrapper,
  InlineNotification,
  NotificationType,
  SelectFormField,
  Tag,
  TagColor,
  TagSize,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import type { TSelectOption } from '@spotted-zebra-uk/ui-components';
import {
  AtsSettings,
  buildStageReasonEntries,
  stageReasonsChanged,
  StageReasonSelection,
  StageReasonIssues,
  findStageReasonIssues,
  StageOption,
  toStageReasonSelection,
  WORKDAY_TOOL,
} from './IntegrationsTab.helpers';
import styles from './IntegrationsTab.module.scss';

const dataTestId = 'stage-rejection-reasons-form';

const TITLE = 'Stage rejection reasons';

const CAPTION =
  'Workday only accepts a rejection reason that is valid at the ' +
  "candidate's current stage. For each stage recruiters reject from, pick " +
  'the reasons Workday accepts there; the reject modal then offers only ' +
  'those. Once any stage is configured, candidates in a stage left ' +
  'unconfigured cannot be rejected from Spotted Zebra. With no stage ' +
  'configured, recruiters see every allowed reason, as before.';

/**
 * With no stage configured recruiters see every allowed reason, so an
 * unconfigured stage only blocks rejection once some other stage has reasons.
 */
function notConfiguredText(selection: StageReasonSelection) {
  const anyStageConfigured = Object.values(selection).some(
    reasonIds => reasonIds.length > 0
  );
  return anyStageConfigured
    ? 'Not configured: candidates at this stage cannot be rejected from Spotted Zebra'
    : 'Not configured';
}

const warningTestId = 'stage-rejection-reasons-warning';

type ReasonOption = { label: string; value: string };

type StageRejectionReasonsCardProps = {
  companyId: number;
  atsIdentifier: string | null | undefined;
  atsSettings: AtsSettings;
  stageOptions: StageOption[];
  stagesLoading: boolean;
  reasonOptions: ReasonOption[];
  reasonsLoading: boolean;
};

/**
 * Names for saved reason ids. An id with no match is a reason deleted in
 * Workday since it was saved; it is shown by id so it can still be seen
 * and removed.
 */
function toReasonOptions(
  reasonIds: readonly string[],
  options: ReasonOption[]
): ReasonOption[] {
  const nameById = new Map(options.map(option => [option.value, option.label]));
  return reasonIds.map(id => ({ value: id, label: nameById.get(id) ?? id }));
}

/**
 * The catalogue's stages, followed by any configured stage no longer in
 * it, labelled by ref so it can still be seen and removed.
 */
function toStageRows(
  selection: StageReasonSelection,
  stageOptions: StageOption[]
): StageOption[] {
  const known = new Set(stageOptions.map(stage => stage.value));
  const removed = Object.entries(selection)
    .filter(([ref, reasonIds]) => !known.has(ref) && reasonIds.length > 0)
    .map(([ref]) => ({ label: ref, value: ref }));
  return [...stageOptions, ...removed];
}

function getStageReasonIssues(
  selection: StageReasonSelection,
  {
    atsSettings,
    stageOptions,
    reasonOptions,
  }: Pick<
    StageRejectionReasonsCardProps,
    'atsSettings' | 'stageOptions' | 'reasonOptions'
  >
) {
  return findStageReasonIssues(selection, {
    stageRefs: stageOptions.map(stage => stage.value),
    reasonIds: reasonOptions.map(reason => reason.value),
    allowedReasonIds: atsSettings.allowedRejectionReasonIds,
  });
}

function UnusableStagesWarning({
  issues,
  stageRows,
}: {
  issues: Record<string, StageReasonIssues>;
  stageRows: StageOption[];
}) {
  const unusable = stageRows.filter(
    stage => issues[stage.value]?.hasNoUsableReason
  );
  if (unusable.length === 0) return null;
  return (
    <div data-testid={warningTestId}>
      <InlineNotification notificationType={NotificationType.WARNING}>
        {`Recruiters cannot reject candidates in these stages, because none of their reasons can be offered: ${unusable
          .map(stage => stage.label)
          .join(', ')}`}
      </InlineNotification>
    </div>
  );
}

function StageWarnings({
  issues,
  reasonOptions,
}: {
  issues: StageReasonIssues | undefined;
  reasonOptions: ReasonOption[];
}) {
  if (!issues) return null;
  const names = (ids: string[]) =>
    toReasonOptions(ids, reasonOptions)
      .map(reason => reason.label)
      .join(', ');
  const warnings = [
    issues.isStageMissing && 'This stage no longer exists in Workday.',
    issues.missingReasonIds.length > 0 &&
      `No longer in Workday, so rejections using it will fail: ${names(
        issues.missingReasonIds
      )}`,
    issues.excludedReasonIds.length > 0 &&
      `Not in the allowed rejection reasons, so never offered: ${names(
        issues.excludedReasonIds
      )}`,
  ].filter((warning): warning is string => Boolean(warning));

  return (
    <>
      {warnings.map(warning => (
        <span
          key={warning}
          className={styles.fieldIdCaption}
          data-testid={warningTestId}
        >
          {warning}
        </span>
      ))}
    </>
  );
}

export function StageRejectionReasonsCard(
  props: StageRejectionReasonsCardProps
) {
  const [isEditing, setIsEditing] = useState(false);

  if (props.atsIdentifier !== WORKDAY_TOOL) return null;

  return isEditing ? (
    <StageRejectionReasonsForm
      {...props}
      onCancelClick={() => setIsEditing(false)}
      onSave={() => setIsEditing(false)}
    />
  ) : (
    <StageRejectionReasonsView
      {...props}
      onEditClick={() => setIsEditing(true)}
    />
  );
}

function StageRejectionReasonsView({
  atsSettings,
  stageOptions,
  stagesLoading,
  reasonOptions,
  onEditClick,
}: StageRejectionReasonsCardProps & { onEditClick: () => void }) {
  const selection = toStageReasonSelection(atsSettings.stageRejectionReasons);
  const stageRows = toStageRows(selection, stageOptions);
  const issues = getStageReasonIssues(selection, {
    atsSettings,
    stageOptions,
    reasonOptions,
  });

  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>{TITLE}</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <UnusableStagesWarning issues={issues} stageRows={stageRows} />
        {!stagesLoading && stageOptions.length === 0 && (
          <span className={styles.fieldIdCaption}>
            No Workday stages have been synced for this company yet.
          </span>
        )}
        {stageRows.map(stage => {
          const reasons = toReasonOptions(
            selection[stage.value] ?? [],
            reasonOptions
          );
          return (
            <div
              key={stage.value}
              data-testid={`stage-rejection-reasons-stage-${stage.label}`}
            >
              <InfoItem
                label={stage.label}
                value={
                  reasons.length > 0 ? (
                    <div className={styles.reasonTags}>
                      {reasons.map(reason => (
                        <Tag
                          key={reason.value}
                          tagColor={TagColor.BLUE}
                          size={TagSize.SMALL}
                          isDismissible={false}
                          isPresentational
                          ariaLabel={reason.label}
                        >
                          {reason.label}
                        </Tag>
                      ))}
                    </div>
                  ) : (
                    notConfiguredText(selection)
                  )
                }
              />
              <StageWarnings
                issues={issues[stage.value]}
                reasonOptions={reasonOptions}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One stage's reasons: the chosen ones as removable tags, the rest behind
 * a searchable dropdown, since the reason list can run to hundreds.
 */
function StageReasonPicker({
  stage,
  reasonOptions,
  selectedIds,
  issues,
  emptyText,
  onChange,
}: {
  stage: StageOption;
  reasonOptions: ReasonOption[];
  selectedIds: string[];
  issues: StageReasonIssues | undefined;
  emptyText: string;
  onChange: (ids: string[]) => void;
}) {
  const selectedSet = new Set(selectedIds);
  const available = reasonOptions.filter(
    option => !selectedSet.has(option.value)
  );

  return (
    <div
      className={styles.resultCard}
      data-testid={`stage-rejection-reasons-stage-${stage.label}`}
    >
      <h4 className={styles.resultCardTitle}>{stage.label}</h4>
      <StageWarnings issues={issues} reasonOptions={reasonOptions} />
      {issues?.isStageMissing && (
        <Button variant="secondary" size="small" onClick={() => onChange([])}>
          Remove stage
        </Button>
      )}
      {selectedIds.length > 0 ? (
        <div className={styles.reasonTags}>
          {toReasonOptions(selectedIds, reasonOptions).map(reason => (
            <Tag
              key={reason.value}
              tagColor={TagColor.BLUE}
              size={TagSize.SMALL}
              ariaLabelDismissPrefix="Remove"
              ariaLabelDismiss={reason.label}
              onDismiss={() =>
                onChange(selectedIds.filter(id => id !== reason.value))
              }
            >
              {reason.label}
            </Tag>
          ))}
        </div>
      ) : (
        <span className={styles.fieldIdCaption}>{emptyText}</span>
      )}
      <SelectFormField
        id={`${dataTestId}__add-${stage.value}`}
        label="Add a reason"
        placeholder={
          available.length > 0
            ? 'Search reasons…'
            : 'All reasons already selected'
        }
        isSearchable
        isDisabled={available.length === 0}
        maxMenuHeight={280}
        options={available}
        onChange={(option: TSelectOption) => {
          if (option?.value) onChange([...selectedIds, option.value]);
        }}
      />
    </div>
  );
}

type StageRejectionReasonsFormValues = { selection: StageReasonSelection };

function StageRejectionReasonsForm({
  companyId,
  atsSettings,
  stageOptions,
  reasonOptions,
  reasonsLoading,
  onCancelClick,
  onSave,
}: StageRejectionReasonsCardProps & {
  onCancelClick: () => void;
  onSave: () => void;
}) {
  const { handleMsgType } = useNotification();
  const [setDispositionConfig] = useSetAtsStageRejectionReasonsMutation();
  const saved = atsSettings.stageRejectionReasons;

  const handleSave = async ({ selection }: StageRejectionReasonsFormValues) => {
    if (!stageReasonsChanged(selection, saved)) {
      onSave();
      return;
    }
    try {
      await setDispositionConfig({
        variables: {
          companyId,
          entries: buildStageReasonEntries(selection, saved),
        },
        refetchQueries: [
          { query: AtsSettingsDocument, variables: { companyId } },
        ],
      });
      handleMsgType({
        type: TNotification.success,
        title: 'Stage rejection reasons updated.',
        dismissable: true,
      });
      onSave();
    } catch {
      handleMsgType({
        type: TNotification.error,
        title: 'Stage rejection reasons could not be updated',
        message: 'Something went wrong. Please try again.',
        dismissable: true,
      });
    }
  };

  return (
    <Formik<StageRejectionReasonsFormValues>
      initialValues={{ selection: toStageReasonSelection(saved) }}
      onSubmit={handleSave}
    >
      {({ submitForm, values, setFieldValue }) => (
        <StageRejectionReasonsFormBody
          selection={values.selection}
          atsSettings={atsSettings}
          stageOptions={stageOptions}
          reasonOptions={reasonOptions}
          reasonsLoading={reasonsLoading}
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          onSelectionChange={selection => setFieldValue('selection', selection)}
        />
      )}
    </Formik>
  );
}

function StageRejectionReasonsFormBody({
  selection,
  atsSettings,
  stageOptions,
  reasonOptions,
  reasonsLoading,
  onCancelClick,
  onSaveClick,
  onSelectionChange,
}: Pick<
  StageRejectionReasonsCardProps,
  'atsSettings' | 'stageOptions' | 'reasonOptions' | 'reasonsLoading'
> & {
  selection: StageReasonSelection;
  onCancelClick: () => void;
  onSaveClick: () => void;
  onSelectionChange: (selection: StageReasonSelection) => void;
}) {
  const stageRows = toStageRows(selection, stageOptions);
  const issues = getStageReasonIssues(selection, {
    atsSettings,
    stageOptions,
    reasonOptions,
  });

  return (
    <EditFormWrapper
      onCancelClick={onCancelClick}
      onSaveClick={onSaveClick}
      title={TITLE}
      formId={dataTestId}
      hasResetAllButton={false}
      data-testid={dataTestId}
    >
      <form
        onSubmit={e => e.preventDefault()}
        id={`${dataTestId}-element`}
        className={styles.form}
      >
        <span className={styles.caption}>{CAPTION}</span>
        <UnusableStagesWarning issues={issues} stageRows={stageRows} />
        {!reasonsLoading && reasonOptions.length === 0 ? (
          <span className={styles.fieldIdCaption}>
            No rejection reasons are available from this company&apos;s ATS.
          </span>
        ) : (
          stageRows.map(stage => (
            <StageReasonPicker
              key={stage.value}
              stage={stage}
              reasonOptions={reasonOptions}
              selectedIds={selection[stage.value] ?? []}
              issues={issues[stage.value]}
              emptyText={notConfiguredText(selection)}
              onChange={ids =>
                onSelectionChange({ ...selection, [stage.value]: ids })
              }
            />
          ))
        )}
      </form>
    </EditFormWrapper>
  );
}
